import { isArray, isString, toUpper, trim } from 'lodash';

/**
 * Names the delivery point of an address: street, line 2, city, state and 5-digit ZIP, trimmed and uppercased.
 *
 * A name or phone change leaves the key alone, and so does a ZIP+4 against its ZIP, so the key answers "is this the
 * same place" for the customer's confirmation and the address book's de-duplication alike.
 *
 * @param {object|null} address - Any address-like object.
 * @returns {string}
 */
export const getAddressDeliveryKey = (address) => [address?.streetAddress1, address?.streetAddress2, address?.city, address?.state, trim(address?.postalCode).slice(0, 5)].map((value) => toUpper(trim(value))).join('|');

/**
 * Which address-book entry a save lands on, and whether the saved entry is the default.
 *
 * An edit lands on the entry it names. Otherwise a repeat of an address already in the book lands on that entry and
 * keeps its default flag, because only an edit of an entry can take the flag away; re-entering the default address
 * would otherwise leave the book with none. Otherwise the save is a new entry. The browser's address list and the
 * server's address book both decide this here, so the two cannot pick different entries.
 *
 * @param {object[]|null} existingAddresses - The address book as it stands.
 * @param {object} address - The address being saved.
 * @param {object} [options]
 * @param {string|null} [options.id] - The entry being edited, when the save is an edit.
 * @param {boolean} [options.isDefault] - Whether the save asks for the entry to be the default.
 * @returns {{ savedEntry: object|null, isEdit: boolean, isDefault: boolean }}
 */
export const resolveSavedEntry = (existingAddresses, address, { id = null, isDefault = false } = {}) => {
	const addresses = isArray(existingAddresses) ? existingAddresses : [];
	const editedEntry = isString(id) && id.length > 0 ? addresses.find((entry) => entry?.id === id) ?? null : null;
	const deliveryKey = getAddressDeliveryKey(address);
	const sameDeliveryPoint = editedEntry === null ? addresses.find((entry) => getAddressDeliveryKey(entry) === deliveryKey) ?? null : null;

	return {
		savedEntry: editedEntry ?? sameDeliveryPoint,
		isEdit: editedEntry !== null,
		isDefault: isDefault === true || sameDeliveryPoint?.isDefault === true,
	};
};
